import React, { createContext, useContext, useState } from "react";
import { logger } from '../../utils/logger';

type PermissionsContextType = {
  permissions: string[];
  setPermissions: (perms: string[]) => void;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissions, setPermissionsState] = useState<string[]>(() => {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    
    try {
      // Handle both array and string formats
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      } else if (typeof parsed === 'string') {
        // Convert comma-separated string to array
        return parsed.split(',').filter(p => p.trim() !== '');
      }
      return [];
    } catch (error) {
      logger.warn('Failed to parse permissions from localStorage:', error);
      return [];
    }
  });

  const setPermissions = (perms: string[]) => {
    setPermissionsState(perms);
    localStorage.setItem("permissions", JSON.stringify(perms));
  };

  return (
    <PermissionsContext.Provider value={{ permissions, setPermissions }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error("usePermissions must be used within a PermissionsProvider");
  return ctx;
}